import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { ELEMENT_TYPE } from 'components/WorkspaceEditor/constants/elementTypes'
import { useProjectSettings } from 'components/WorkspaceEditor/Workspace2Store'
import { UnitsOfMeasure, UnitsOfMeasureKey } from 'models/projectSettings'
import { formatSelectionOption } from 'pages/ProjectsPage/WorkspacePage/components/Common/ContentItemElementText'
import { useMemo } from 'react'

/** Regular expression pattern to match and remove Units of Measure delimiters */
const UOM_DELIMITER_PATTERN = /\|START_IP\||\|END_IP\||\|START_SI\||\|END_SI\|/g

/** Return type for the useFormatReplacementOption hook */
interface UseFormatReplacementOptionReturn {
    /** The formatted text with child elements combined */
    formattedText: string
    /** Function to update content items with UOM elements */
    getUpdatedContentItem: (
        contentItem: Partial<TextContentElement>
    ) => Partial<TextContentElement>[]
    /** Function to remove UOM delimiters from text */
    removeUnitOfMeasureDelimiters: (text: string | undefined) => string
}

/**
 * Removes Units of Measure delimiter markers from text content.
 * These delimiters (|START_IP|, |END_IP|, |START_SI|, |END_SI|) are used internally
 * to mark imperial and metric unit boundaries but should not be displayed to users.
 *
 * @param text - The text content that may contain UOM delimiters
 * @returns The text with all UOM delimiters removed
 */
export const removeUnitOfMeasureDelimiters = (text: string | undefined): string => {
    return text?.replace(UOM_DELIMITER_PATTERN, '') || ''
}

/**
 * Processes a single UOM element by separating imperial and metric values.
 * 
 * @param element - The UOM element to process
 * @param contentText - The remaining text to extract values from
 * @returns Updated element with separated values and remaining text
 */
const processUOMElement = (
    element: Partial<TextContentElement>, 
    contentText: string,
    unitOfMeasureSetting: UnitsOfMeasure
) => {
    if (element.type !== ELEMENT_TYPE.UNITS_OF_MEASURE || !contentText || !element.imperialUnits) {
        return { element, contentText }
    }

    let imperial = element.imperialUnits
    let metric = element.metricUnits
    
    switch(unitOfMeasureSetting.toString()){
        case UnitsOfMeasureKey.IMPERIAL:
            imperial = contentText
            break;
        case UnitsOfMeasureKey.METRIC:
            metric = contentText
            break;
        case UnitsOfMeasureKey.BOTH:
            const match = contentText.match(/^(.*?)\s*\(([^)]+)\)\s*$/)
            if (match) {
                imperial = match[1].trim()
                metric = match[2].trim()
            }
            else {
                imperial = contentText
            }
            break;
    }

    return {
        element: { ...element, imperialUnits: imperial, metricUnits: metric },
        contentText: contentText
    }
}

/**
 * Processes elements within a replacement option that contain Units of Measure.
 * Simplified version that handles text and UOM elements efficiently.
 * 
 * @param contentItem - The content item containing elements to process
 * @returns Array of updated elements with properly separated values
 */
export const processUnitsOfMeasureElements = (
    contentItem: Partial<TextContentElement>,
    unitOfMeasureSetting: UnitsOfMeasure
): Partial<TextContentElement>[] => {
    const elements = contentItem?.elements
    if (!elements?.some(el => el.type === ELEMENT_TYPE.UNITS_OF_MEASURE)) {
        return []
    }

    let contentText = contentItem.text || ''
    const processedElements: Partial<TextContentElement>[] = []
    
    for (const element of elements) {
        if (element.type === ELEMENT_TYPE.TEXT && contentText.includes(element.text!)) {
            contentText = contentText.replace(element.text!, '')
            processedElements.push(element)
        } else if (element.type === ELEMENT_TYPE.UNITS_OF_MEASURE) {
            const { element: updatedElement, contentText: newRemainingText } = 
                processUOMElement(element, contentText, unitOfMeasureSetting)
            processedElements.push(updatedElement)
        }
    }

    return processedElements
}

/**
 * Processes replacement text by applying formatting and removing UOM delimiters.
 * 
 * @param contentItem - The content item to process
 * @param unitOfMeasure - The unit of measure preference
 * @returns Processed text string
 */
const processReplacementText = (
    contentItem: Partial<TextContentElement>, 
    unitOfMeasure: string
): string => {
    const formatted = formatSelectionOption(contentItem, unitOfMeasure.toLowerCase())
    return removeUnitOfMeasureDelimiters(formatted || contentItem.text?.trim() || '')
}

/**
 * Custom hook to format ReplacementOption text by combining main text with child elements.
 * Handles Units of Measure based on project settings and provides utilities for text processing.
 *
 * @param contentItem - The ReplacementOption content item to format
 * @returns Object containing formatted text and utility functions
 */
export const useFormatReplacementOption = (
    contentItem: Partial<TextContentElement>
): UseFormatReplacementOptionReturn => {
    const projectSettings = useProjectSettings()
    const unitOfMeasureSetting = projectSettings?.unitOfMeasure ?? UnitsOfMeasure.IMPERIAL
    const formattedText = useMemo(() => {
        if (!contentItem) return ''

        try {
            return processReplacementText(contentItem, unitOfMeasureSetting)
        } catch (error) {
            return contentItem.text?.trim() || ''
        }
    }, [contentItem, projectSettings])

    return {
        formattedText,
        getUpdatedContentItem: (contentItem) => processUnitsOfMeasureElements(contentItem, unitOfMeasureSetting),
        removeUnitOfMeasureDelimiters,
    }
}
